<template>
  <v-container fluid>
    <v-card-actions>
      <div class="hidden-sm-and-down">
        <setup-skills-dialog-create @created="loadSkills" />
      </div>
      <v-spacer /><v-spacer /><v-spacer /><v-spacer /><v-spacer /><v-spacer />
      <v-autocomplete
        :items="categories"
        :loading="loadingCategories"
        class="hidden-sm-and-down"
        placeholder="Skill Categories"
        label="Category"
        item-text="name"
        item-value="id"
        color="primary"
        hide-no-data
        hide-selected
        clearable        
        @change="filter"
      />
    </v-card-actions>
    <v-row>
      <v-col
        v-for="skill in filteredSkills"
        :key="skill.id"
        cols="12"
        md="6"
        xl="4"
      >
        <v-hover>
          <v-card 
            slot-scope="{ hover }" 
            :class="`elevation-${hover ? 12 : 2}`"
          >
            <v-container fluid>
              <v-row>
                <v-col
                  class="pt-0" 
                  cols="4"
                >
                  <helper-image-input  
                    v-model="images[skill.id]"              
                    :max-size-in-mb="2"
                    data-vv-name="image"
                    field-name="image"
                    accept-type=".svg"  
                    extension="ext:svg"            
                  >  
                    <v-card-title 
                      slot="activator"
                      primary-title 
                    >
                      <v-img
                        :src="images[skill.id] ? images[skill.id].imageURL : skill.image_url"
                        class="cursor-pointer"
                        height="100px"
                        contain
                      />          
                    </v-card-title>                            
                  </helper-image-input>
                  <div 
                    v-if="images[skill.id]"
                    class="text-center"
                  >         
                    <v-btn 
                      :disabled="saving"
                      text
                      icon
                      @click="resetImageHolder(skill.id)"
                    >
                      <v-icon size="30">
                        mdi-close-circle-outline
                      </v-icon>
                    </v-btn>     
                    <v-btn 
                      :loading="saving"
                      text
                      icon
                      color="primary"
                      @click="uploadImage(skill.id)"
                    >
                      <v-icon size="30">
                        mdi-cloud-upload
                      </v-icon>
                    </v-btn>
                  </div>
                </v-col>
                <v-col
                  class="pt-0" 
                  cols="8"
                >
                  <v-card-title
                    style="max-width: 250px;"
                    class="text-capitalize d-inline-block text-truncate"
                  >
                    {{ skill.name }}
                  </v-card-title>
                  <v-card-subtitle class="pb-0">
                    {{ skill.category.name }}
                  </v-card-subtitle>
                  <v-rating
                    :length="skill.levels_count"
                    :value="skill.levels_count"
                    color="yellow darken-1"
                    class="px-1"
                    readonly
                  />
                </v-col>
              </v-row>
            </v-container>
            <v-divider light />
            <v-card-actions v-if="!images[skill.id]">
              <div>
                <setup-skills-dialog-edit
                  :skill="skill"
                  @updated="loadSkills"
                />
                <setup-skills-dialog-delete 
                  :skill="skill"
                  @deleted="loadSkills"
                />    
              </div>             
              <v-spacer />
              <v-btn 
                icon 
                @click="onClickHandler(skill.id)"
              >
                <v-icon>{{ shows[skill.id] ? 'keyboard_arrow_down' : 'keyboard_arrow_up' }}</v-icon>
              </v-btn>
            </v-card-actions>
            <v-slide-y-transition>
              <v-card-text v-show="shows[skill.id]">
                <v-toolbar 
                  flat
                  color="white"
                >
                  <v-toolbar-title>Skill Levels</v-toolbar-title>
                  <v-divider
                    class="mx-2"
                    inset
                    vertical
                  />
                  <v-spacer />
                  <setup-skills-levels-dialog-create 
                    v-if="skillLevels[skill.id] && skillLevels[skill.id].length < 5"
                    :skill="skill"    
                    :level="skillLevels[skill.id].length + 1"     
                    @created="loadSkillLevels(skill.id, true); loadSkills()"
                  />
                </v-toolbar>
                <v-data-table
                  :headers="headers"
                  :items="skillLevels[skill.id]"
                  :loading="loadingLevels[skill.id]"
                  hide-default-footer
                  hide-default-header
                  class="elevation-1"
                >
                  <template v-slot:item.description="{ item }">
                    <v-edit-dialog
                      :return-value.sync="item.description"
                      large
                      persistent
                      @save="updateLevel(item)"
                    >
                      {{ item.description }}
                      <template v-slot:input>
                        <v-textarea
                          v-model="item.description"
                          :rows="3"
                          :rules="[max250chars]"
                          label="Edit"
                          counter
                        />
                      </template>
                    </v-edit-dialog>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-slide-y-transition>
          </v-card>        
        </v-hover>
      </v-col>
    </v-row>
    <v-row
      v-show="filteredSkills.length === 0 && loading === false"
      justify="center"
    >
      <v-col
        class="text-center"
        cols="12"
      >
        <div class="headline">
          <span class="blue-grey--text">No Data Available</span>
        </div>
      </v-col>   
    </v-row>    
  </v-container>
</template>

<script>
  export default {
    components: {
      SetupSkillsDialogCreate: () => import('@/components/setup/skills/DialogCreate'),
      HelperImageInput: () => import('@/components/helper/ImageInput'),
      SetupSkillsDialogEdit: () => import('@/components/setup/skills/DialogEdit'),
      SetupSkillsDialogDelete: () => import('@/components/setup/skills/DialogDelete'),
      SetupSkillsLevelsDialogCreate: () => import('@/components/setup/skills/Levels/DialogCreate'),
    },
    metaInfo () {
      return {
        title: 'Setup - Skills',
      }
    },
    data () {
      return {
        max250chars: v => v.length <= 250 || 'Input too long!',
        headers: [
          {
            text: 'Level',
            align: 'left',
            sortable: false,
            value: 'level',
          },
          {
            text: 'Description',
            align: 'left',
            sortable: false,
            value: 'description',
          },
        ],
        loading: true,
        loadingCategories: false,
        loadingLevels: [],
        saving: false,
        categoryFilterId: null,
        skills: [],
        skillLevels: [],
        categories: [],
        images: [],
        shows: [],
      }
    },
    computed: {
      filteredSkills () {
        return this.skills.filter(skill => {
          if (this.categoryFilterId) {
            return skill.skill_category_id === this.categoryFilterId
          }        

          return skill
        })
      },
    },
    created () {   
      this.loadSkills()
      this.loadCategories()
    },
    methods: {
      onClickHandler (id) {
        this.$set(this.shows, id, !this.shows[id])

        this.loadSkillLevels(id)
      },
      loadSkillLevels (skillId, forceRefresh = false) {
        if ((this.skillLevels[skillId] || []).length > 0 && !forceRefresh) return

        this.$set(this.loadingLevels, skillId, true)

        this.$api.skillLevels().list(skillId)
          .then(response => {
            this.$set(this.skillLevels, skillId, response.data)

            this.$set(this.loadingLevels, skillId, false)
          })
      },
      loadSkills () {
        this.$api.skills().list()
          .then(response => {
            this.skills = response.data
            this.loading = false
          })      
      },
      loadCategories () {
        this.loadingCategories = true

        this.$api.skillCategories().list()
          .then(response => {
            this.categories = response.data

            this.loadingCategories = false
          })
      },    
      uploadImage (id) {
        this.saving = true

        this.$api.skills().uploadImage(id, this.images[id].formData)
          .then(response => {
            if (response.status === 200) {
              this.savedImage(id)
            }
          })
          .catch(() => {
            this.$snack({
              color: 'error',
              message: 'Photo upload failed, please try again',
            })
          }) 
          .then(() => {
            this.saving = false
          })
      },
      updateLevel (skillLevel) {
        this.$api.skillLevels().update(skillLevel.skill_id, skillLevel.id, { description: skillLevel.description })
          .then(response => {
            this.$snack({ message: response.data.status })
          })
      },
      savedImage (id) {     
        this.images[id] = null

        this.$snack({
          message: 'Photo uploaded successfully!',
        })

        this.loadSkills()
      },
      resetImageHolder (id) {
        this.$set(this.images, id, null)
      },
      filter (value) {
        this.categoryFilterId = value
      },
    },
  }
</script>

<style scoped>
.cursor-pointer{
  cursor: pointer;
}
</style>
